/* RecycleBin — /admin/recycle-bin
   - Sorted newest to oldest (deleted_at desc)
   - Case column = case_type + case_ref
   - Red styling when days_in_bin >= 25
   - file_name displayed correctly (hook aliases file_name→filename)
   - Bulk select, restore, permanent delete */
import { useState, useMemo } from 'react';
import css from '../AdminPage.module.css';
import {
  useRecycleBin,
  useRecycleBinStats,
  useRestoreDocument,
  usePermanentDelete,
} from '../../../hooks/useAdmin';
import { useToast } from '../../../hooks/useToast';

export default function RecycleBin() {
  const { data: items = [], isLoading } = useRecycleBin();
  const { data: stats } = useRecycleBinStats();
  const restore = useRestoreDocument();
  const permDelete = usePermanentDelete();
  const { showToast } = useToast();

  const ROWS_PER_PAGE = 15;
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [rbPage, setRbPage] = useState(1);
  const [detailItem, setDetailItem] = useState<(typeof items)[0] | null>(null);

  /* Sort newest-first, then filter by search */
  const filtered = useMemo(() => {
    const sorted = [...items].sort(
      (a, b) => new Date(b.deleted_at).getTime() - new Date(a.deleted_at).getTime(),
    );
    if (!search) return sorted;
    const q = search.toLowerCase();
    return sorted.filter(
      (i) =>
        (i.file_name || i.filename || '').toLowerCase().includes(q) ||
        (i.case_ref || '').toLowerCase().includes(q) ||
        (i.case_type || '').toLowerCase().includes(q),
    );
  }, [items, search]);

  const totalPages = Math.ceil(filtered.length / ROWS_PER_PAGE);
  const paged = filtered.slice((rbPage - 1) * ROWS_PER_PAGE, rbPage * ROWS_PER_PAGE);

  function toggleSelect(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((i) => i.id)));
  }

  async function handleRestore(id: number) {
    try {
      await restore.mutateAsync(id);
      setSelected((prev) => {
        const n = new Set(prev);
        n.delete(id);
        return n;
      });
      showToast('Document restored', 'success');
    } catch {
      showToast('Failed to restore', 'error');
    }
  }

  async function handlePermanentDelete(id: number) {
    if (!confirm('Permanently delete this document? This cannot be undone.')) return;
    try {
      await permDelete.mutateAsync(id);
      setSelected((prev) => {
        const n = new Set(prev);
        n.delete(id);
        return n;
      });
      showToast('Permanently deleted', 'success');
    } catch {
      showToast('Failed to delete', 'error');
    }
  }

  async function bulkRestore() {
    for (const id of selected) await handleRestore(id);
  }

  async function bulkDelete() {
    if (!confirm(`Permanently delete ${selected.size} documents?`)) return;
    for (const id of selected) {
      try {
        await permDelete.mutateAsync(id);
      } catch {
        /* continue */
      }
    }
    setSelected(new Set());
    showToast('Bulk delete complete', 'success');
  }

  if (isLoading) {
    return (
      <div className={css.spinner}>
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
      </div>
    );
  }

  return (
    <>
      {stats && (
        <div className={css.statsGrid}>
          <div className={css.statCard}>
            <span className={css.statValue}>{stats.total}</span>
            <span className={css.statLabel}>Total</span>
          </div>
          <div className={css.statCard}>
            <span className={css.statValue}>{stats.active}</span>
            <span className={css.statLabel}>Active</span>
          </div>
          <div className={css.statCard}>
            <span className={css.statValue}>{stats.restored}</span>
            <span className={css.statLabel}>Restored</span>
          </div>
          <div className={css.statCard}>
            <span
              className={css.statValue}
              style={{ color: stats.expiring_soon > 0 ? 'var(--error-600)' : undefined }}
            >
              {stats.expiring_soon}
            </span>
            <span className={css.statLabel}>Expiring Soon</span>
          </div>
        </div>
      )}

      <div className={css.section}>
        <div className={css.sectionHeader}>
          <h2 className={css.sectionTitle}>🗑️ Document Recycle Bin</h2>
          <div className={css.toolbar}>
            <div className={css.searchWrapper}>
              <span className={css.searchIcon}>🔍</span>
              <input
                className={css.searchInput}
                placeholder="Search files or case refs..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {selected.size > 0 && (
              <>
                <button className={css.btnSecondary} onClick={bulkRestore}>
                  Restore ({selected.size})
                </button>
                <button className={css.btnDanger} onClick={bulkDelete}>
                  Delete ({selected.size})
                </button>
              </>
            )}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className={css.emptyState}>
            <div className={css.emptyIcon}>🗑️</div>
            <p className={css.emptyMessage}>Recycle bin is empty</p>
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table className={css.table} style={{ minWidth: 900 }}>
                <thead>
                  <tr>
                    <th style={{ width: 32 }}>
                      <input
                        type="checkbox"
                        checked={selected.size === paged.length && paged.length > 0}
                        onChange={toggleAll}
                      />
                    </th>
                    <th>File Name</th>
                    <th>Case</th>
                    <th>Deleted By</th>
                    <th>Deleted</th>
                    <th>Days</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((item) => {
                    const days = item.days_in_bin ?? 0;
                    const isExpiring = days >= 25;
                    const rowStyle: React.CSSProperties = isExpiring
                      ? {
                          background: 'var(--error-50)',
                          borderLeft: '3px solid var(--error-600)',
                          cursor: 'pointer',
                        }
                      : { cursor: 'pointer' };

                    return (
                      <tr
                        key={item.id}
                        style={rowStyle}
                        onClick={() => setDetailItem(item)}
                        title="Click to view details"
                      >
                        <td onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selected.has(item.id)}
                            onChange={() => toggleSelect(item.id)}
                          />
                        </td>
                        <td style={{ fontWeight: 500 }}>
                          {item.file_name || item.filename || '—'}
                        </td>
                        <td>
                          {item.case_type
                            ? `${item.case_type} — ${item.case_ref}`
                            : item.case_ref || '—'}
                        </td>
                        <td>{item.deleted_by || '—'}</td>
                        <td style={{ fontSize: 'var(--font-size-xs)', whiteSpace: 'nowrap' }}>
                          {new Date(item.deleted_at).toLocaleDateString()}
                        </td>
                        <td>
                          <span
                            style={{
                              fontWeight: 600,
                              color: isExpiring
                                ? 'var(--error-600)'
                                : days >= 15
                                  ? 'var(--warning-600)'
                                  : 'var(--gray-600)',
                            }}
                          >
                            {days}d
                          </span>
                        </td>
                        <td>
                          <span
                            className={
                              item.status === 'active' ? css.badgeWarning : css.badgeSuccess
                            }
                          >
                            {item.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className={css.pagination}>
                <button
                  className={css.pageBtn}
                  disabled={rbPage === 1}
                  onClick={() => setRbPage(rbPage - 1)}
                >
                  ‹ Prev
                </button>
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-500)' }}>
                  Page {rbPage} of {totalPages} ({filtered.length} total)
                </span>
                <button
                  className={css.pageBtn}
                  disabled={rbPage >= totalPages}
                  onClick={() => setRbPage(rbPage + 1)}
                >
                  Next ›
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail / action modal */}
      {detailItem && (
        <div className={css.modalBackdrop} onClick={() => setDetailItem(null)}>
          <div className={css.modal} onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className={css.modalHeader}>
              <h3 className={css.modalTitle}>Document Details</h3>
              <button className={css.modalClose} onClick={() => setDetailItem(null)}>
                ×
              </button>
            </div>
            <div className={css.modalBody}>
              <div style={{ display: 'grid', gap: 10 }}>
                <div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-400)' }}>
                    File Name
                  </div>
                  <div style={{ fontWeight: 600 }}>
                    {detailItem.file_name || detailItem.filename || '—'}
                  </div>
                </div>
                <div className={css.grid2}>
                  <div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-400)' }}>
                      Case
                    </div>
                    <div>
                      {detailItem.case_type
                        ? `${detailItem.case_type} — ${detailItem.case_ref}`
                        : detailItem.case_ref || '—'}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-400)' }}>
                      Status
                    </div>
                    <span
                      className={
                        detailItem.status === 'active' ? css.badgeWarning : css.badgeSuccess
                      }
                    >
                      {detailItem.status}
                    </span>
                  </div>
                </div>
                <div className={css.grid2}>
                  <div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-400)' }}>
                      Deleted By
                    </div>
                    <div>{detailItem.deleted_by || '—'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-400)' }}>
                      Deleted
                    </div>
                    <div>{new Date(detailItem.deleted_at).toLocaleString()}</div>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-400)' }}>
                    Days in Bin
                  </div>
                  <div
                    style={{
                      fontWeight: 600,
                      color:
                        (detailItem.days_in_bin ?? 0) >= 25
                          ? 'var(--danger-600, #dc2626)'
                          : undefined,
                    }}
                  >
                    {detailItem.days_in_bin ?? 0} days
                  </div>
                </div>
              </div>
            </div>
            <div className={css.modalFooter}>
              <button
                className={css.btnSecondary}
                onClick={() => {
                  handleRestore(detailItem.id);
                  setDetailItem(null);
                }}
                disabled={detailItem.status === 'restored'}
              >
                Restore
              </button>
              <button
                className={css.btnDanger}
                onClick={() => {
                  handlePermanentDelete(detailItem.id);
                  setDetailItem(null);
                }}
              >
                Permanently Delete
              </button>
              <button className={css.btnSecondary} onClick={() => setDetailItem(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
